import { supabase } from '../../lib/supabase';
import { PostgrestError } from '@supabase/supabase-js';

export interface LotLeaseSummary {
  lot_id: string;
  community_name: string;
  lot_perm_use: string;
  lot_avail: boolean;
  lease_start: string | null;
  lease_end: string | null;
  holder_name: string | null;
  review_date: string | null;
  rental_amount: number | null;
  lease_rent_freq: string | null;
  // Additional fields from subleases table
  lease_id?: number | null;
  lease_method?: string | null;
  lease_type?: string | null;
  lease_hmth_term?: string | null;
  lease_dealing?: string | null;
  lease_furth_term?: string | null;
  lease_deal_doc?: string | null;
  lease_holder?: number | null;
  leaseholder_name?: string | null;
}

export const supabaseLotLeaseSummaryService = {
  getAll: async () => {
    try {
      console.log('Fetching lot lease summary data...');
      const { data, error } = await supabase
        .from('lot_lease_summary')
        .select('*')
        .order('community_name', { ascending: true })
        .order('lot_id', { ascending: true });

      if (error) {
        const pgError = error as PostgrestError;
        
        // Log structured error information
        console.error('Error fetching lot lease summary:', {
          message: pgError.message,
          details: pgError.details,
          hint: pgError.hint,
          code: pgError.code
        });
        
        // Try to safely stringify the full error object
        try {
          const errorStr = JSON.stringify(error, (key, value) => {
            if (value instanceof Error) {
              const { name, message, stack, ...rest } = value;
              return {
                ...rest,
                name,
                message,
                stack
              };
            }
            return value;
          }, 2);
          console.debug('Full error object:', errorStr);
        } catch (jsonError) {
          console.debug('Could not stringify full error:', error.toString());
        }
        
        // Log request context
        console.debug('Request context:', {
          table: 'lot_lease_summary',
          operation: 'SELECT *',
          orderBy: ['community_name', 'lot_id'],
          schema: 'public',
          timestamp: new Date().toISOString()
        });
        
        throw new Error(
          `Failed to fetch lot lease summary: ${pgError.message}` +
          `${pgError.hint ? ` (Hint: ${pgError.hint})` : ''}` +
          `${pgError.details ? ` (Details: ${pgError.details})` : ''}` +
          ` (Code: ${pgError.code || 'unknown'})`
        );
      }

      return data as LotLeaseSummary[];
    } catch (error) {
      console.error('Error in getAll:', error);
      throw error;
    }
  },

  getByLotId: async (lotId: string) => {
    try {
      const { data, error } = await supabase
        .from('lot_lease_summary')
        .select('*')
        .eq('lot_id', lotId)
        .single();

      if (error) {
        console.error('Error fetching lot lease summary:', error);
        throw error;
      }

      return data as LotLeaseSummary;
    } catch (error) {
      console.error('Error in getByLotId:', error);
      throw error;
    }
  },

  getByCommunity: async (communityName: string) => {
    try {
      const { data, error } = await supabase
        .from('lot_lease_summary')
        .select('*')
        .eq('community_name', communityName)
        .order('lot_id', { ascending: true });

      if (error) {
        console.error('Error fetching lot lease summary:', error);
        throw error;
      }

      return data as LotLeaseSummary[];
    } catch (error) {
      console.error('Error in getByCommunity:', error);
      throw error;
    }
  },

  create: async (lease: Omit<LotLeaseSummary, 'review_date'>) => {
    try {
      const { data, error } = await supabase
        .from('lot_lease_summary')
        .insert([lease])
        .select()
        .single();

      if (error) {
        console.error('Error creating lot lease:', error);
        throw error;
      }

      return data as LotLeaseSummary;
    } catch (error) {
      console.error('Error in create:', error);
      throw error;
    }
  },

  update: async (lotId: string, lease: Partial<LotLeaseSummary>) => {
    try {
      const { data, error } = await supabase
        .from('lot_lease_summary')
        .update(lease)
        .eq('lot_id', lotId)
        .select()
        .single();

      if (error) {
        console.error('Error updating lot lease:', error);
        throw error;
      }

      return data as LotLeaseSummary;
    } catch (error) {
      console.error('Error in update:', error);
      throw error;
    }
  }
};
